<template>
  <div class="modal-overlay">
    <div class="modal-container" @click.stop ref="modalRef">
      <div class="modal-header">
        <h1 class="modal-title">Create Technician</h1>
        <button class="close-btn" @click="handleClose">
          <img :src="closeFillIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="modal-body">
        <form @submit.prevent="handleSubmit">
          <!-- Name -->
          <div class="form-group">
            <label class="form-label"> <span class="required">*</span> Name </label>
            <div class="input-wrapper" :class="{ error: errors.name }">
              <input
                v-model="formData.name"
                type="text"
                class="form-input"
                placeholder="Enter name"
                :maxlength="techFieldLimits.name"
                @blur="handleNameBlur"
                @input="handleNameInput"
              />
              <img v-if="errors.name" :src="errorIcon" alt="error" class="error-icon" />
            </div>
          </div>

          <!-- Mobile number -->
          <div class="form-group">
            <label class="form-label">
              <span class="required">*</span> Mobile number (used to log in)
            </label>
            <div class="mobile-input-wrapper" :class="{ error: errors.mobile }">
              <span class="country-code">+1</span>
              <input
                :value="formData.mobile"
                type="tel"
                inputmode="numeric"
                pattern="[0-9]*"
                class="form-input mobile-input"
                placeholder="Enter mobile number"
                :maxlength="techFieldLimits.phone"
                @input="handleMobileInput"
                @blur="handleMobileBlur"
              />
              <img v-if="errors.mobile" :src="errorIcon" alt="error" class="error-icon" />
            </div>
            <span v-if="errors.mobile" class="error-text"
              >Please enter a 10-digit mobile number</span
            >
          </div>

          <!-- Upload avatar -->
          <div class="form-group">
            <label class="form-label"> <span class="required">*</span> Upload avatar </label>
            <div class="upload-container" :class="{ error: errors.avatar }">
              <div v-if="!previewUrl" class="upload-area" @click="triggerUpload">
                <img :src="photoIcon" alt="photo" class="upload-icon" />
                <span class="upload-text">Upload</span>
              </div>
              <div v-else class="uploaded-image-wrapper">
                <img :src="previewUrl" alt="uploaded" class="uploaded-image" />
                <div class="delete-overlay" @click="removeImage">
                  <img :src="deleteIcon" alt="delete" class="delete-icon" />
                  <span class="delete-text">Delete</span>
                </div>
              </div>
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="file-input"
                @change="handleFileUpload"
              />
            </div>
          </div>

          <!-- Role -->
          <div class="form-row">
            <div class="form-group half">
              <label class="form-label"> <span class="required">*</span> Role </label>
              <div class="custom-select-wrapper" :class="{ error: errors.role }" @focusout="handleRoleFocusOut">
                <div
                  class="custom-select"
                  tabindex="0"
                  :class="{ 'is-open': dropdownOpen === 'role' }"
                  @click="toggleDropdown('role')"
                >
                  <span class="custom-select-value" :class="{ 'placeholder-text': !formData.role }">
                    {{ formData.role || 'Select role' }}
                  </span>
                  <div class="select-trail">
                    <img v-if="errors.role" :src="errorIcon" alt="error" class="error-icon-select" />
                    <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                  </div>
                </div>
                <div v-if="dropdownOpen === 'role'" class="custom-dropdown">
                  <div
                    v-for="option in roleOptions"
                    :key="option"
                    class="dropdown-item"
                    :class="{ selected: formData.role === option }"
                    @click="selectOption('role', option)"
                  >
                    {{ option }}
                  </div>
                </div>
              </div>
            </div>
            <div class="form-group half">
              <label class="form-label"><span style="visibility: hidden">*</span></label>
              <div class="input-wrapper" :class="{ error: errors.licenceNo }">
                <input
                  v-model="formData.licenceNo"
                  type="text"
                  class="form-input"
                  placeholder="Enter licence No."
                  @blur="handleLicenceBlur"
                  @input="handleLicenceInput"
                />
                <img v-if="errors.licenceNo" :src="errorIcon" alt="error" class="error-icon" />
              </div>
            </div>
          </div>

          <!-- SIN# -->
          <div class="form-row">
            <div class="form-group half">
              <label class="form-label"> <span class="required">*</span> SIN#/HST# </label>
              <div class="custom-select-wrapper" :class="{ error: errors.sinType }" @focusout="handleSinTypeFocusOut">
                <div
                  class="custom-select"
                  tabindex="0"
                  :class="{ 'is-open': dropdownOpen === 'sinType' }"
                  @click="toggleDropdown('sinType')"
                >
                  <span class="custom-select-value" :class="{ 'placeholder-text': !formData.sinType }">
                    {{ formData.sinType || 'Select SIN type' }}
                  </span>
                  <div class="select-trail">
                    <img v-if="errors.sinType" :src="errorIcon" alt="error" class="error-icon-select" />
                    <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                  </div>
                </div>
                <div v-if="dropdownOpen === 'sinType'" class="custom-dropdown">
                  <div
                    v-for="option in sinTypeOptions"
                    :key="option"
                    class="dropdown-item"
                    :class="{ selected: formData.sinType === option }"
                    @click="selectOption('sinType', option)"
                  >
                    {{ option }}
                  </div>
                </div>
              </div>
            </div>
            <div class="form-group half">
              <label class="form-label">
                <span style="visibility: hidden; user-select: none">*</span>
              </label>
              <div class="input-wrapper" :class="{ error: errors.sin }">
                <input
                  v-model="formData.sin"
                  type="text"
                  class="form-input"
                  placeholder="Enter Tax Number"
                  :maxlength="techFieldLimits.sinNumber"
                  @blur="handleSinBlur"
                  @input="handleSinInput"
                />
                <img v-if="errors.sin" :src="errorIcon" alt="error" class="error-icon" />
              </div>
            </div>
          </div>

          <!-- Residency status -->
          <div class="form-group">
            <label class="form-label"> <span class="required">*</span> Residency status </label>
            <div class="custom-select-wrapper" :class="{ error: errors.residencyStatus }" @focusout="handleResidencyFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'residencyStatus' }"
                @click="toggleDropdown('residencyStatus')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.residencyStatus }">
                  {{ formData.residencyStatus || 'Select residency status' }}
                </span>
                <div class="select-trail">
                  <img
                    v-if="errors.residencyStatus"
                    :src="errorIcon"
                    alt="error"
                    class="error-icon-select"
                  />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'residencyStatus'" class="custom-dropdown">
                <div
                  v-for="option in residencyOptions"
                  :key="option"
                  class="dropdown-item"
                  :class="{ selected: formData.residencyStatus === option }"
                  @click="selectOption('residencyStatus', option)"
                >
                  {{ option }}
                </div>
              </div>
            </div>
          </div>

          <!-- Address -->
          <div class="form-group">
            <label class="form-label"> <span class="required">*</span> Address </label>
            <div class="input-wrapper" :class="{ error: errors.address }">
              <input
                v-model="formData.address"
                type="text"
                class="form-input"
                placeholder="Enter address"
                :maxlength="techFieldLimits.address"
                @blur="handleAddressBlur"
                @input="handleAddressInput"
              />
              <img v-if="errors.address" :src="errorIcon" alt="error" class="error-icon" />
            </div>
          </div>

          <!-- Email -->
          <div class="form-group">
            <label class="form-label"> <span class="required">*</span> Email </label>
            <div class="input-wrapper" :class="{ error: errors.email }">
              <input
                v-model="formData.email"
                type="email"
                class="form-input"
                placeholder="Enter Email"
                :maxlength="techFieldLimits.email"
                @blur="handleEmailBlur"
                @input="handleEmailInput"
              />
              <img v-if="errors.email" :src="errorIcon" alt="error" class="error-icon" />
            </div>
          </div>
        </form>
      </div>

      <!-- Action buttons -->
      <div class="form-actions">
        <button type="button" class="btn btn-cancel" @click="handleCancel">Cancel</button>
        <button type="button" class="btn btn-submit" :disabled="submitting" @click="handleSubmit">
          Submit
        </button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, reactive, inject, onMounted, onUnmounted } from 'vue'
import request from '@/api/request'

import photoIcon from '@/assets/photo.png'
import downarrowIcon from '@/assets/downarrow.png'
import errorIcon from '@/assets/alert.png'
import deleteIcon from '@/assets/delete_white@2x.png'
import closeFillIcon from '@/assets/Fill 1@2x.png'
import '@/styles/custom-select-trail.css'
import { FIELD_LIMITS } from '@/constants/fieldLimits'
import { validateFieldLimits, sanitizeDigitsOnly } from '@/utils/fieldLimitValidation'

const techFieldLimits = FIELD_LIMITS.technician

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()
const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
  () => {},
)

const fileInput = ref<HTMLInputElement | null>(null)
const modalRef = ref<HTMLDivElement | null>(null)
const submitting = ref(false)
const dropdownOpen = ref<string | null>(null)
const previewUrl = ref<string>('')

const roleOptions = ['Inspector', 'Technician']
const sinTypeOptions = ['Individual', 'Business']
const residencyOptions = ['Canadian Citizenship', 'Permanent Resident', 'Work Permit']

const formData = reactive({
  name: '',
  mobile: '',
  avatar: '' as string | File,
  role: '',
  licenceNo: '',
  sinType: '',
  sin: '',
  residencyStatus: '',
  address: '',
  email: '',
})

const errors = reactive({
  name: false,
  mobile: false,
  avatar: false,
  role: false,
  licenceNo: false,
  sinType: false,
  sin: false,
  residencyStatus: false,
  address: false,
  email: false,
})

function isEmpty(value: string | undefined | null): boolean {
  return !String(value ?? '').trim()
}

function isFocusLeavingWrapper(event: FocusEvent): boolean {
  const wrapper = event.currentTarget as HTMLElement
  return !wrapper.contains(event.relatedTarget as Node)
}

function handleNameBlur() {
  errors.name = isEmpty(formData.name)
}

function handleNameInput() {
  if (!isEmpty(formData.name)) errors.name = false
}

function handleMobileBlur() {
  errors.mobile = isEmpty(formData.mobile) || !/^\d{10}$/.test(formData.mobile)
}

function handleLicenceBlur() {
  errors.licenceNo = false
}

function handleLicenceInput() {
  errors.licenceNo = false
}

function handleSinBlur() {
  errors.sin = isEmpty(formData.sin)
}

function handleSinInput() {
  if (!isEmpty(formData.sin)) errors.sin = false
}

function handleAddressBlur() {
  errors.address = isEmpty(formData.address)
}

function handleAddressInput() {
  if (!isEmpty(formData.address)) errors.address = false
}

function handleEmailBlur() {
  errors.email = isEmpty(formData.email)
}

function handleEmailInput() {
  if (!isEmpty(formData.email)) errors.email = false
}

function handleRoleFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.role = isEmpty(formData.role)
}

function handleSinTypeFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.sinType = isEmpty(formData.sinType)
}

function handleResidencyFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.residencyStatus = isEmpty(formData.residencyStatus)
}

function triggerUpload() {
  fileInput.value?.click()
}

function handleMobileInput(event: Event) {
  formData.mobile = sanitizeDigitsOnly(
    (event.target as HTMLInputElement).value,
    techFieldLimits.phone,
  )
  errors.mobile = false
}

function handleFileUpload(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    formData.avatar = file
    previewUrl.value = URL.createObjectURL(file)
    errors.avatar = false
  }
}

function removeImage() {
  formData.avatar = ''
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
    previewUrl.value = ''
  }
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

function validateDropdownField(fieldName: string): void {
  if (fieldName === 'role') errors.role = isEmpty(formData.role)
  else if (fieldName === 'sinType') errors.sinType = isEmpty(formData.sinType)
  else if (fieldName === 'residencyStatus') errors.residencyStatus = isEmpty(formData.residencyStatus)
}

function toggleDropdown(fieldName: string) {
  const wasOpen = dropdownOpen.value === fieldName
  dropdownOpen.value = wasOpen ? null : fieldName
  if (wasOpen) validateDropdownField(fieldName)
}

function selectOption(fieldName: string, value: string) {
  formData[fieldName as keyof typeof formData] = value
  dropdownOpen.value = null
  if (fieldName === 'role') errors.role = false
  if (fieldName === 'sinType') errors.sinType = false
  if (fieldName === 'residencyStatus') errors.residencyStatus = false
}

function closeDropdowns(event: MouseEvent) {
  const target = event.target as HTMLElement
  if (!target.closest('.custom-select-wrapper')) {
    if (dropdownOpen.value) {
      validateDropdownField(dropdownOpen.value)
      dropdownOpen.value = null
    }
  }
}

onMounted(() => {
  modalRef.value?.addEventListener('click', closeDropdowns)
})

onUnmounted(() => {
  modalRef.value?.removeEventListener('click', closeDropdowns)
})

function validateForm(): boolean {
  let isValid = true

  Object.keys(errors).forEach((key) => {
    errors[key as keyof typeof errors] = false
  })

  if (!formData.name) {
    errors.name = true
    isValid = false
  }
  if (!formData.mobile || !/^\d{10}$/.test(formData.mobile)) {
    errors.mobile = true
    isValid = false
  }
  if (!previewUrl.value && !formData.avatar) {
    errors.avatar = true
    isValid = false
  }
  if (!formData.role) {
    errors.role = true
    isValid = false
  }
  if (!formData.sinType) {
    errors.sinType = true
    isValid = false
  }
  if (!formData.sin) {
    errors.sin = true
    isValid = false
  }
  if (!formData.residencyStatus) {
    errors.residencyStatus = true
    isValid = false
  }
  if (!formData.address) {
    errors.address = true
    isValid = false
  }
  if (isEmpty(formData.email)) {
    errors.email = true
    isValid = false
  }
  if (!isValid) {
    return false
  }

  const limitErr = validateFieldLimits([
    { value: formData.name, max: techFieldLimits.name, label: 'Name' },
    { value: formData.mobile, max: techFieldLimits.phone, label: 'Mobile number' },
    { value: formData.sin, max: techFieldLimits.sinNumber, label: 'SIN number' },
    { value: formData.address, max: techFieldLimits.address, label: 'Address' },
    { value: formData.email, max: techFieldLimits.email, label: 'Email' },
  ])
  if (limitErr) {
    showToast('warning', limitErr)
    return false
  }

  return true
}

interface UploadResponse {
  code?: number
  msg?: string
  data?: {
    url?: string
    path?: string
  }
}

interface CreateResponse {
  code?: number
  msg?: string
}

async function handleSubmit() {
  if (!validateForm()) {
    showToast('warning', 'Please complete all required fields.')
    return
  }

  submitting.value = true
  try {
    let avatarUrl = ''

    // 1. 处理头像上传
    if (formData.avatar instanceof File) {
      const formDataUpload = new FormData()
      formDataUpload.append('category', 'image')
      formDataUpload.append('file', formData.avatar)

      const uploadRes = await request.post<UploadResponse>('/api/admin/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      if (uploadRes.code === 200 || uploadRes.msg === 'successfully.') {
        avatarUrl = uploadRes.data?.url || uploadRes.data?.path || ''
      } else {
        submitting.value = false
        showToast('error', uploadRes.msg || 'Avatar upload failed')
        return
      }
    } else if (typeof formData.avatar === 'string' && formData.avatar) {
      avatarUrl = formData.avatar
    }

    if (!avatarUrl) {
      errors.avatar = true
      showToast('warning', 'Please complete all required fields.')
      return
    }

    const payload = {
      name: formData.name,
      phone_code: '1',
      phone: formData.mobile,
      avatar: avatarUrl,
      work_role: formData.role.toLowerCase(),
      license_no: formData.licenceNo.trim() || undefined,
      sin_type: formData.sinType.toLowerCase(),
      sin_number: formData.sin,
      residency_status: formData.residencyStatus.toLowerCase().replace(/\s+/g, '_'),
      address: formData.address,
      email: formData.email,
      settlement_config: {
        essential_care: {
          type: 'percentage_sba',
          value: 0,
        },
        enhanced_care: {
          type: 'percentage_sba',
          value: 0,
        },
        premium_care: {
          type: 'hourly',
          value: 0,
        },
      },
    }

    // 3. 发送创建请求
    const res = await request.post<CreateResponse>('/api/admin/technician/create', payload)
    if (res.msg === 'successfully.' || res.code === 200) {
      showToast('success', 'Successfully saved')
      emit('saved')
      closeDelay()
    } else {
      showToast('error', res.msg || 'Save failed')
    }
  } catch (err) {
    console.error(err)
    showToast('error', 'Network error or request failed')
  } finally {
    submitting.value = false
  }
}

function handleClose() {
  emit('close')
}
function closeDelay() {
  setTimeout(() => emit('close'), 2100)
}
function handleCancel() {
  emit('close')
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.modal-container {
  background: #fff;
  border-radius: 12px;
  width: 530px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 24px 0; /* 上24 | 左右24 | 下16px */
  flex-shrink: 0;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  white-space: nowrap;
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.modal-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 0 24px;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #cccccc;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #cccccc;
}

.form-group {
  margin-bottom: 16px;
}

.form-group.half {
  margin-bottom: 16px;
}

.required {
  color: #e54848;
  margin-right: 4px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

/* 核心对齐逻辑：统一高度与内边距 */
.form-input,
.custom-select {
  height: 32px;
  line-height: 20px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  width: 100%;
  color: #000000;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper.error {
  border: 1px solid #ff4d4f;
  border-radius: 100px;
}

.form-input {
  padding: 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  outline: none;
  transition: border-color 0.2s;
  background: #fff;
}

.form-input:hover {
  border-color: #e0e0e0;
}

.form-input:focus {
  border-color: #666666;
}

.form-input::placeholder {
  color: #cccccc;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.input-wrapper.error .form-input {
  border: none;
  height: 32px;
}

.mobile-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  overflow: hidden;
  height: 32px;
  transition: border-color 0.2s;
}


.mobile-input-wrapper:hover {
  border-color: #e0e0e0;
}

.mobile-input-wrapper:focus-within {
  border-color: #666666;
}

.mobile-input-wrapper.error {
  border-color: #ff4d4f;
}

.country-code {
  position: relative;
  padding: 0 12px;
  height: 100%;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  white-space: nowrap;
}

.country-code::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 16px;
  background-color: #e8e8e8;
}

.mobile-input {
  border: none;
  border-radius: 0;
  padding-left: 8px;
}

.upload-container {
  position: relative;
}

.upload-container.error .upload-area,
.upload-container.error .uploaded-image-wrapper {
  border-color: #ff4d4f;
}

.upload-container.error .upload-area {
  background: #fffafa;
}

.upload-area {
  width: 96px;
  height: 96px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: border-color 0.2s;
  background: #fff;
}

.upload-icon {
  width: 32px;
  height: 32px;
}

.upload-text {
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.uploaded-image-wrapper {
  position: relative;
  width: 96px;
  height: 96px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
}

.uploaded-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.delete-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}

.uploaded-image-wrapper:hover .delete-overlay {
  opacity: 1;
}

.delete-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.delete-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #ffffff;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.file-input {
  display: none;
}

.custom-select-wrapper {
  position: relative;
}

.custom-select-wrapper.error {
  border: 1px solid #ff4d4f;
  border-radius: 100px;
}

.custom-select {
  padding: 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s;
  user-select: none;
  display: flex;
  align-items: center;
}

.custom-select:hover,
.custom-select:focus {
  border-color: #e0e0e0;
  outline: none;
}

.custom-select.is-open {
  border-color: #666666;
}

.custom-select-wrapper.error .custom-select {
  border: none;
  height: 32px;
}

.placeholder-text {
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #cccccc;
  line-height: 20px;
}
.select-arrow {
  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg)
    brightness(100%) contrast(100%);
  transition: transform 0.2s ease;
}

.custom-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
  border: none;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dropdown-item {
  padding: 8px 16px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: #000000;
  cursor: pointer;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.custom-dropdown .dropdown-item.selected {
  font-weight: 600;
}

.error-icon {
  position: absolute;
  right: 12px;
  width: 16px;
  height: 16px;
  object-fit: contain;
  pointer-events: none;
}

.error-text {
  display: block;
  margin-top: 6px;
  font-size: 0.85rem;
  color: #ff4d4f;
}

/* 并列行布局 */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 16px;
  align-items: flex-start;
}

.form-row .form-group.half {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
}

.form-row .form-group.half:last-child {
  padding-top: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 8px 24px 24px;
  background: #fff;
  flex-shrink: 0;
}

.btn {
  width: 80px;
  height: 32px;
  padding: 0;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.btn-cancel {
  background: #fff;
  border: 1px solid #000000;
  color: #000000;
}

.btn-cancel:hover {
  background: #000000;
  color: #ffffff;
}

.btn-submit {
  background: #000000;
  color: #fff;
}

.btn-submit:hover {
  background: #484848;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .modal-body {
    padding: 16px;
  }
}
</style>
